import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Toaster } from 'react-hot-toast';

// Import components
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import POS from './pages/POS/POS';
import EndOfDayReport from './pages/POS/EndOfDayReport';
import TestEOD from './pages/POS/TestEOD';
import SimpleTest from './pages/POS/SimpleTest';
import Customers from './pages/Customers/Customers';
import CustomerDetail from './pages/Customers/CustomerDetail';
import OrderDetail from './pages/Orders/OrderDetail';
import PendingJobs from './pages/Orders/PendingJobs';
import Dashboard from './pages/Admin/Dashboard';
import ServiceManagement from './pages/Admin/ServiceManagement';
import SiteManagement from './pages/Admin/SiteManagement';
import UserManagement from './pages/Admin/UserManagement';
import Expenses from './pages/Admin/Expenses';
import InventoryList from './pages/Inventory/InventoryList';
import FinancialReportsDashboard from './pages/FinancialReports/FinancialReportsDashboard';
import ModuleManagement from './pages/Admin/ModuleManagement';
import ModuleAccessErrorPage from './pages/ModuleAccessErrorPage';
import SitePricingManagement from './pages/Admin/SitePricingManagement';
import InvoiceManagement from './pages/Admin/InvoiceManagement';
import SiteInvoiceView from './pages/Admin/SiteInvoiceView';
import BillingPage from './pages/BillingPage';
import ApiUsageReport from './pages/Admin/ApiUsageReport';
import LoyaltyManagement from './pages/Admin/LoyaltyManagement';
import DiscountManagement from './pages/Admin/DiscountManagement';
import Reports from './pages/Admin/Reports';

// Import contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { StripeProvider } from './contexts/StripeContext';
import OfflineIndicator from './components/OfflineIndicator';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Loading spinner shown while auth state is being hydrated
const AuthLoading: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
  </div>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: string }> = ({ children, requiredRole }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return <AuthLoading />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/pos" replace />;
  }
  return <>{children}</>;
};

// Admin-only Protected Route (admin + super_admin)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return <AuthLoading />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Navigate to="/pos" replace />;
  }
  return <>{children}</>;
};

// Super Admin only route (sites & users management)
const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return <AuthLoading />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== 'super_admin') {
    return <Navigate to="/pos" replace />;
  }
  return <>{children}</>;
};

// Public Route Component (accessible when not authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return <AuthLoading />;
  }
  if (!isAuthenticated) {
    return <>{children}</>;
  }
  // Redirect based on user role
  if (user?.role === 'admin' || user?.role === 'super_admin') {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/pos" replace />;
};

function AppRoutes() {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <AuthLoading />;
  }

  const getDefaultRoute = () => {
    if (!isAuthenticated) return '/login';
    if (user?.role === 'admin' || user?.role === 'super_admin') return '/admin';
    return '/pos';
  };

  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } />

        {/* Protected Routes */}
        <Route path="/pos" element={
          <ProtectedRoute>
            <POS />
          </ProtectedRoute>
        } />
        <Route path="/pos/end-of-day" element={
          <AdminRoute>
            <EndOfDayReport />
          </AdminRoute>
        } />
        <Route path="/pos/test-eod" element={
          <ProtectedRoute>
            <TestEOD />
          </ProtectedRoute>
        } />
        <Route path="/pos/simple-test" element={
          <ProtectedRoute>
            <SimpleTest />
          </ProtectedRoute>
        } />
        <Route path="/customers" element={
          <ProtectedRoute>
            <Customers />
          </ProtectedRoute>
        } />
        <Route path="/customers/:id" element={
          <ProtectedRoute>
            <CustomerDetail />
          </ProtectedRoute>
        } />
        <Route path="/orders/:id" element={
          <ProtectedRoute>
            <OrderDetail />
          </ProtectedRoute>
        } />
        <Route path="/jobs" element={
          <ProtectedRoute>
            <PendingJobs />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <AdminRoute>
            <Dashboard />
          </AdminRoute>
        } />
        <Route path="/admin/services" element={
          <AdminRoute>
            <ServiceManagement />
          </AdminRoute>
        } />
        <Route path="/admin/sites" element={
          <SuperAdminRoute>
            <SiteManagement />
          </SuperAdminRoute>
        } />
        <Route path="/admin/users" element={
          <SuperAdminRoute>
            <UserManagement />
          </SuperAdminRoute>
        } />
        <Route path="/admin/customers" element={
          <AdminRoute>
            <Customers />
          </AdminRoute>
        } />
        <Route path="/admin/expenses" element={
          <AdminRoute>
            <Expenses />
          </AdminRoute>
        } />
        <Route path="/admin/inventory" element={
          <AdminRoute>
            <InventoryList />
          </AdminRoute>
        } />
        <Route path="/admin/financial-reports" element={
          <AdminRoute>
            <FinancialReportsDashboard />
          </AdminRoute>
        } />
        <Route path="/admin/modules" element={
          <SuperAdminRoute>
            <ModuleManagement />
          </SuperAdminRoute>
        } />
        <Route path="/admin/pricing" element={
          <SuperAdminRoute>
            <SitePricingManagement />
          </SuperAdminRoute>
        } />
        <Route path="/admin/invoices" element={
          <SuperAdminRoute>
            <InvoiceManagement />
          </SuperAdminRoute>
        } />
        <Route path="/admin/api-usage" element={
          <SuperAdminRoute>
            <ApiUsageReport />
          </SuperAdminRoute>
        } />
        <Route path="/admin/billing" element={
          <AdminRoute>
            <BillingPage />
          </AdminRoute>
        } />
        <Route path="/admin/loyalty" element={
          <AdminRoute>
            <LoyaltyManagement />
          </AdminRoute>
        } />
        <Route path="/admin/discounts" element={
          <AdminRoute>
            <DiscountManagement />
          </AdminRoute>
        } />
        <Route path="/admin/reports" element={
          <AdminRoute>
            <Reports />
          </AdminRoute>
        } />
        <Route path="/module-access-error" element={<ModuleAccessErrorPage />} />

        {/* Default Routes */}
        <Route index element={<Navigate to={getDefaultRoute()} replace />} />
        <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
      </Routes>

      {/* Global Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <StripeProvider>
        <AuthProvider>
          <Router>
            <AppRoutes />
            <OfflineIndicator />
          </Router>
        </AuthProvider>
      </StripeProvider>
    </QueryClientProvider>
  );
}

export default App;
